import { useState, useCallback } from 'react';
import { StatusTimeline, StatusTimelineFilteredEvent } from '../../types/statusTimeline';
import { Mixpanel } from '../../utils/mixpanel';
import { useAppSelector } from '../../app/hooks';
import { getStatusTimeline } from './StatusTimelineApiHelper';
import { DEAL, MANDATE } from '../../store/investor/reducer';

export interface UseStatusTimelineProps {
    recordType: 'LEAD' | 'DEAL' | 'MANDATE';
    baseURL?: string;
}

export const useStatusTimeline = ({ recordType, baseURL }: UseStatusTimelineProps) => {
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [statusTimeline, setStatusTimeline] = useState<StatusTimeline | null>(null);
    const [loading, setLoading] = useState(false);

    const userData = useAppSelector((state) => state.user.userData);
    const platformData = useAppSelector((state) => state.user.domainMetadata);
    const dealsStatusConfig = useAppSelector((state) => state.investor.deals.dealStatusConfig);

    // Function to fetch status timeline data from API
    const fetchStatusTimeline = useCallback(
        async (recordId: string, companyName: string): Promise<StatusTimeline | false> => {
            return getStatusTimeline({
                referenceId: recordId,
                recordType,
                companyName,
                baseURL,
                dealStatusDropdownOptions: dealsStatusConfig?.[MANDATE] || [],
            });
        },
        [recordType, baseURL, dealsStatusConfig],
    );

    const handleStatusTimelineClick = useCallback(
        async (row: any) => {
            try {
                setLoading(true);
                setIsDrawerOpen(true);

                // Get record ID and company name based on record type
                const recordId =
                    recordType === 'LEAD' ? row.assesseeOrgId : recordType === 'DEAL' ? row.dealId : row.mandateId || row.dealId; // mandate might use dealId

                const companyName =
                    recordType === 'LEAD' ? row.orgName : recordType === 'DEAL' ? row.borrowerName : row.companyName || row.borrowerName;

                const timeline = await fetchStatusTimeline(recordId, companyName);

                // Handle the API response - set to null if API call failed
                if (timeline) {
                    setStatusTimeline(timeline);
                } else {
                    setStatusTimeline(null);
                    console.error('Failed to fetch status timeline');
                    // Could show error toast here
                }
            } catch (error) {
                console.error('Error fetching status timeline:', error);
                setStatusTimeline(null);
                // Could show error toast here
            } finally {
                setLoading(false);
            }
        },
        [fetchStatusTimeline, recordType],
    );

    const handleDrawerClose = useCallback(() => {
        setIsDrawerOpen(false);
        setStatusTimeline(null);
        setLoading(false);
    }, []);

    const trackFilterEvent = useCallback(
        (fromDate?: string, toDate?: string) => {
            const mixpanelEvent: StatusTimelineFilteredEvent = {
                page: recordType === 'LEAD' ? 'Leads' : recordType === 'DEAL' ? 'Deals' : 'Mandate',
                currentUrl: window.location.href,
                user_email: userData?.primaryEmail || '',
                from_date: fromDate,
                to_date: toDate,
            };

            Mixpanel.track('status_timeline_filtered', mixpanelEvent);
        },
        [recordType, userData],
    );

    return {
        isDrawerOpen,
        statusTimeline,
        loading,
        handleStatusTimelineClick,
        handleDrawerClose,
        trackFilterEvent,
    };
};
